"use client";

import { useState } from "react";
import Link from "next/link";
import { PRICING_FAQ } from "./pricingFaq";
import {
  TIERS,
  type PlanTier as Tier,
  startCheckout,
  openPortal,
} from "@/components/billing/tiers";
import { Button } from "@/components/marketing/ui/Button";
import { Badge } from "@/components/marketing/ui/Badge";
import { Eyebrow } from "@/components/marketing/ui/Eyebrow";

const FAQ = PRICING_FAQ;

function Price({ tier, yearly, featured }: { tier: Tier; yearly: boolean; featured?: boolean }) {
  if (tier.monthly === 0) {
    return (
      <div className="flex items-baseline gap-1">
        <span className="font-display text-5xl font-semibold">Free</span>
      </div>
    );
  }
  // Yearly shows the per-month equivalent of the annual charge, rounded.
  const perMonth = yearly ? Math.round(tier.yearlyTotal / 12) : tier.monthly;
  return (
    <div>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${featured ? "text-white/70" : "text-ink-soft"}`}>
          $
        </span>
        <span className="font-display text-5xl font-semibold tabular-nums">{perMonth}</span>
        <span className={`text-sm font-bold ${featured ? "text-white/70" : "text-ink-soft"}`}>
          USD / mo
        </span>
      </div>
      <p
        className={`mt-1 h-4 text-xs font-semibold ${featured ? "text-gold-light" : "text-gold-deep"}`}
      >
        {yearly ? `billed $${tier.yearlyTotal}/year` : "billed monthly"}
      </p>
    </div>
  );
}

function Check() {
  return (
    <svg viewBox="0 0 20 20" className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" fill="none">
      <circle cx="10" cy="10" r="10" fill="var(--gold)" opacity="0.2" />
      <path
        d="M6 10.5l2.5 2.5L14 7.5"
        stroke="var(--gold)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TierCard({ tier, yearly }: { tier: Tier; yearly: boolean }) {
  const featured = tier.featured;

  // Featured CTA uses an inverted red-pen style with no matching primitive variant — left as styled element.
  const featuredCtaClass =
    "mt-6 rounded-full px-5 py-3 text-center text-sm font-bold transition-transform hover:-translate-y-0.5 bg-red text-paper-2";

  return (
    <div
      className={`relative flex flex-col rounded-3xl border p-7 ${
        featured
          ? "border-transparent bg-ink text-paper-2 shadow-[0_30px_70px_-40px_rgba(32,28,43,0.8)] lg:-translate-y-3"
          : "border-line bg-paper-2"
      }`}
    >
      {featured && (
        <Badge
          variant="filled"
          className="absolute -top-3 left-7 px-3! font-black! tracking-wide uppercase"
        >
          Most popular
        </Badge>
      )}
      <h3 className="font-display text-2xl font-semibold">{tier.name}</h3>
      <p className={`mt-1 text-sm ${featured ? "text-white/70" : "text-ink-soft"}`}>{tier.blurb}</p>
      <div className="mt-6">
        <Price tier={tier} yearly={yearly} featured={featured} />
      </div>

      {featured ? (
        // Inverted red-pen CTA — no matching Button variant; left as styled element.
        tier.href ? (
          <Link href={tier.href} className={featuredCtaClass}>
            {tier.cta}
          </Link>
        ) : (
          <button
            onClick={() =>
              tier.tierKey && startCheckout(tier.tierKey, yearly ? "yearly" : "monthly")
            }
            className={featuredCtaClass}
          >
            {tier.cta}
          </button>
        )
      ) : tier.href ? (
        <Button href={tier.href} className="mt-6 w-full px-5! py-3! text-sm! shadow-sm!">
          {tier.cta}
        </Button>
      ) : (
        <Button
          onClick={() => tier.tierKey && startCheckout(tier.tierKey, yearly ? "yearly" : "monthly")}
          className="mt-6 w-full px-5! py-3! text-sm! shadow-sm!"
        >
          {tier.cta}
        </Button>
      )}

      <ul className="mt-7 space-y-3 text-sm">
        {tier.features.map((f) => (
          <li key={f} className="flex gap-2.5">
            <Check />
            <span className={featured ? "text-white/85" : "text-ink"}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-lg font-semibold">{q}</span>
        <span
          className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border border-line text-lg transition-transform ${
            open ? "rotate-45 bg-gold text-paper-2" : "text-ink-soft"
          }`}
        >
          +
        </span>
      </button>
      <div
        className="grid transition-all duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="pr-10 pb-5 text-[15px] leading-relaxed text-ink-soft">{a}</p>
        </div>
      </div>
    </div>
  );
}

export function PricingClient() {
  const [yearly, setYearly] = useState(true);

  return (
    <div className="mx-auto max-w-6xl px-6 pt-6 pb-8 lg:pt-10">
      {/* header */}
      <div className="mx-auto max-w-2xl text-center">
        <Eyebrow>Simple, honest pricing</Eyebrow>
        <h1 className="font-display mt-4 text-4xl leading-tight font-medium sm:text-5xl">
          Pay for the writing, <span className="italic">not the hype.</span>
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-ink-soft">
          Start free. Upgrade when the coaching is paying for itself. Cancel the moment it
          isn&apos;t.
        </p>
      </div>

      {/* billing toggle */}
      <div className="mt-9 flex items-center justify-center gap-4">
        <span className={`text-sm font-bold ${!yearly ? "text-ink" : "text-ink-soft"}`}>
          Monthly
        </span>
        <button
          onClick={() => setYearly((v) => !v)}
          role="switch"
          aria-checked={yearly}
          aria-label="Toggle yearly billing"
          className="relative h-8 w-14 rounded-full border border-line bg-paper-2 transition-colors"
        >
          <span
            className="absolute top-1 h-6 w-6 rounded-full bg-ink shadow transition-all"
            style={{ left: yearly ? "1.75rem" : "0.25rem" }}
          />
        </button>
        <span className={`text-sm font-bold ${yearly ? "text-ink" : "text-ink-soft"}`}>Yearly</span>
        <Badge className="font-black!">2 months free</Badge>
      </div>

      {/* tiers */}
      <div className="mt-12 grid items-start gap-5 lg:grid-cols-3">
        {TIERS.map((t) => (
          <TierCard key={t.name} tier={t} yearly={yearly} />
        ))}
      </div>

      {/* manage subscription */}
      <div className="mt-4 text-center">
        <button
          onClick={openPortal}
          className="text-sm text-ink-soft underline decoration-[var(--line)] underline-offset-2 hover:text-ink"
        >
          Manage subscription
        </button>
      </div>

      <p className="mt-4 text-center text-sm text-ink-soft">
        Prices in USD. Coaching a class or a household?{" "}
        <Link
          href="/for-schools"
          className="font-bold text-ink underline decoration-[var(--gold)] decoration-2 underline-offset-2"
        >
          Ask about seats
        </Link>
        .
      </p>

      {/* FAQ */}
      <div id="faq" className="mx-auto mt-24 max-w-3xl">
        <h2 className="font-display text-center text-3xl font-medium sm:text-4xl">
          Questions, answered
        </h2>
        <div className="mt-8">
          {FAQ.map((item) => (
            <FaqItem key={item.q} {...item} />
          ))}
        </div>
      </div>
    </div>
  );
}
